import React from 'react';
import { BookOpen, ExternalLink, Code, Phone, Bot, Zap, FileText, Video } from 'lucide-react';

const DocsPage = () => {
  const docSections = [
    {
      title: 'Getting Started',
      icon: Zap,
      color: 'text-green-400',
      docs: [
        { title: 'Quick Start Guide', desc: 'Set up your first AI agent in 5 minutes' },
        { title: 'Dashboard Overview', desc: 'Learn your way around the platform' },
        { title: 'Account Setup', desc: 'Configure your account settings' },
      ]
    },
    {
      title: 'Voice Agents',
      icon: Bot,
      color: 'text-purple-400',
      docs: [
        { title: 'Creating Voice Agents', desc: 'Build and configure AI voice assistants' },
        { title: 'Conversational Flows', desc: 'Design conversation scripts' },
        { title: 'Voice Selection', desc: 'Choose and customize voices' },
        { title: 'Language Support', desc: 'Configure multi-language agents' },
      ]
    },
    {
      title: 'Calling Features',
      icon: Phone,
      color: 'text-blue-400',
      docs: [
        { title: 'Inbound Calls', desc: 'Handle incoming customer calls' },
        { title: 'Outbound Campaigns', desc: 'Set up auto-dialer campaigns' },
        { title: 'Call Transfer', desc: 'Transfer calls to live agents' },
        { title: 'Call Recording', desc: 'Record and review calls' },
      ]
    },
    {
      title: 'API Reference',
      icon: Code,
      color: 'text-amber-400',
      docs: [
        { title: 'REST API Overview', desc: 'API authentication and basics' },
        { title: 'Webhooks', desc: 'Receive real-time event notifications' },
        { title: 'Voice Agent API', desc: 'Programmatically manage agents' },
        { title: 'Call Logs API', desc: 'Access call data and analytics' },
      ]
    },
  ];

  const resources = [
    { title: 'Video Tutorials', icon: Video, link: '#', desc: 'Watch step-by-step guides' },
    { title: 'API Docs', icon: Code, link: '#', desc: 'Complete API reference' },
    { title: 'Blog', icon: FileText, link: '#', desc: 'Tips and best practices' },
  ];

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2">Documentation</h1>
        <p className="text-gray-400">Learn how to get the most out of AIConneqtors</p>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        {resources.map((resource) => (
          <a
            key={resource.title}
            href={resource.link}
            className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 hover:border-teal-500/50 transition-colors group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-teal-500/20 rounded-xl flex items-center justify-center group-hover:bg-teal-500/30 transition-colors">
                <resource.icon className="w-6 h-6 text-teal-400" />
              </div>
              <div>
                <p className="text-white font-medium group-hover:text-teal-400 transition-colors">{resource.title}</p>
                <p className="text-gray-500 text-sm">{resource.desc}</p>
              </div>
              <ExternalLink className="w-5 h-5 text-gray-600 ml-auto group-hover:text-teal-400 transition-colors" />
            </div>
          </a>
        ))}
      </div>

      {/* Documentation Sections */}
      <div className="space-y-8">
        {docSections.map((section) => (
          <div key={section.title} className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-6">
              <section.icon className={`w-6 h-6 ${section.color}`} />
              <h2 className="text-xl font-semibold text-white">{section.title}</h2>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              {section.docs.map((doc) => (
                <a
                  key={doc.title}
                  href="#"
                  className="flex items-start gap-3 p-4 bg-[#1a1a1f] border border-gray-700 rounded-lg hover:border-gray-600 transition-colors group"
                >
                  <BookOpen className="w-5 h-5 text-gray-500 mt-0.5 group-hover:text-teal-400 transition-colors" />
                  <div>
                    <p className="text-white font-medium group-hover:text-teal-400 transition-colors">{doc.title}</p>
                    <p className="text-gray-500 text-sm">{doc.desc}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Help Section */}
      <div className="mt-8 bg-gradient-to-r from-purple-600/20 to-indigo-600/20 border border-purple-500/30 rounded-xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-white mb-2">Need Help?</h3>
            <p className="text-gray-400">Can't find what you're looking for? Our support team is here to help.</p>
          </div>
          <button className="px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocsPage;
